import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Send, Trash2, Users2, TrendingUp, Mail, MessageSquare, Phone, Megaphone, Sparkles, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../utils/helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNELS = [
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
  { value: 'sms', label: 'SMS', icon: Phone },
];

const emptyCampaign = {
  name: '', channel: 'whatsapp', subject: '', body: '',
  audience: {
    audience_type: 'clients', product_type: '', lead_status: '',
    location: '', select_all: true, explicit_ids: [],
  },
  scheduled_for: '',
};

const Campaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyCampaign);
  const [audiencePreview, setAudiencePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendingId, setSendingId] = useState('');
  const [starterTemplates, setStarterTemplates] = useState([]);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/campaigns`, { withCredentials: true });
      setCampaigns(data);
    } catch (e) {
      console.error('Load campaigns failed:', e);
      toast.error(e.response?.data?.detail || 'Failed to load campaigns');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get(`${API}/campaign-templates/starter`, { withCredentials: true })
      .then(r => setStarterTemplates(r.data))
      .catch(() => setStarterTemplates([]));
  }, []);

  const applyStarterTemplate = (tmpl) => {
    setForm(f => ({
      ...f,
      name: f.name || tmpl.name,
      channel: tmpl.channel || f.channel,
      subject: tmpl.subject || f.subject,
      body: tmpl.body,
    }));
    setTemplatePickerOpen(false);
    toast.success(`Loaded template: ${tmpl.name}`);
  };

  const previewAudience = async () => {
    try {
      const { data } = await axios.post(`${API}/campaigns/preview-audience`, form.audience, { withCredentials: true });
      setAudiencePreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Audience preview failed');
    }
  };

  const saveDraft = async (sendNow) => {
    if (!form.name.trim()) { toast.error('Give the campaign a name'); return; }
    if (!form.body.trim()) { toast.error('Message body cannot be empty'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, channel: form.channel,
        subject: form.channel === 'email' ? (form.subject || form.name) : null,
        body: form.body,
        audience: form.audience,
        scheduled_for: form.scheduled_for || null,
      };
      const { data } = await axios.post(`${API}/campaigns`, payload, { withCredentials: true });
      toast.success('Campaign saved');
      if (sendNow) {
        setSendingId(data.campaign_id);
        const res = await axios.post(`${API}/campaigns/${data.campaign_id}/send`, {}, { withCredentials: true });
        toast.success(`Sent to ${res.data.sent} · ${res.data.failed} failed · ${res.data.skipped} skipped`);
      }
      setDialogOpen(false);
      setForm(emptyCampaign);
      setAudiencePreview(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save campaign');
    } finally { setSaving(false); setSendingId(''); }
  };

  const sendExisting = async (id) => {
    setSendingId(id);
    try {
      const { data } = await axios.post(`${API}/campaigns/${id}/send`, {}, { withCredentials: true });
      const total = (data.sent || 0) + (data.failed || 0) + (data.skipped || 0);
      if (total === 0) {
        toast.warning('No recipients matched this audience filter. Widen filters or pick more clients/leads.', { duration: 7000 });
      } else if (data.sent === 0 && data.skipped > 0) {
        toast.warning(`0 sent · ${data.failed} failed · ${data.skipped} skipped. Common cause: Twilio not configured. Go to Settings → Twilio.`, {
          duration: 9000,
          action: { label: 'Fix Twilio', onClick: () => { window.location.href = '/settings'; } },
        });
      } else {
        toast.success(`Sent: ${data.sent} · Failed: ${data.failed} · Skipped: ${data.skipped}`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setSendingId(''); }
  };

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailCampaign, setDetailCampaign] = useState(null);
  const openDetail = async (id) => {
    try {
      const { data } = await axios.get(`${API}/campaigns/${id}`, { withCredentials: true });
      setDetailCampaign(data);
      setDetailOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load campaign'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    try {
      await axios.delete(`${API}/campaigns/${id}`, { withCredentials: true });
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const updateAudience = (patch) => setForm(f => ({ ...f, audience: { ...f.audience, ...patch } }));

  const badgeForStatus = (status) => {
    if (status === 'sent') return 'bg-green-100 text-green-800';
    if (status === 'scheduled') return 'bg-blue-100 text-blue-800';
    return 'bg-slate-100 text-slate-700';
  };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="spinner" /></div>;

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Campaigns</h1>
          <p className="text-slate-600 mt-1">Reach clients and leads by Email, WhatsApp or SMS</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setForm(emptyCampaign); setAudiencePreview(null); } }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-800 hover:bg-blue-900" data-testid="new-campaign-button">
              <Plus className="h-4 w-4 mr-2" /> New Campaign
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>New Campaign</DialogTitle>
              <DialogDescription>Build audience, pick channel + template, then send or schedule.</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Campaign Name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="campaign-name-input" />
                </div>
                <div className="space-y-2"><Label>Channel *</Label>
                  <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                    <SelectTrigger data-testid="campaign-channel-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CHANNELS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {form.channel === 'email' && (
                <div className="space-y-2"><Label>Email Subject</Label>
                  <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder={form.name || 'Subject'} data-testid="campaign-subject-input" />
                </div>
              )}

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Message Body *</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setTemplatePickerOpen(true)}
                    className="h-7 text-xs"
                    data-testid="campaign-use-template-button"
                  >
                    <Sparkles className="h-3.5 w-3.5 mr-1 text-purple-600" /> Use starter template
                  </Button>
                </div>
                <textarea
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  rows={5}
                  maxLength={2000}
                  placeholder="Hi {client_name}, ..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-800 text-sm"
                  data-testid="campaign-body-input"
                />
                <p className="text-[11px] text-slate-500">Tokens: <code>{'{client_name}'}</code>, <code>{'{agent_name}'}</code>. {form.body.length}/2000</p>
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3" data-testid="audience-section">
                <p className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Users2 className="h-4 w-4 text-blue-700" /> Audience</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Type</Label>
                    <Select value={form.audience.audience_type} onValueChange={(v) => updateAudience({ audience_type: v })}>
                      <SelectTrigger data-testid="audience-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="clients">Clients</SelectItem>
                        <SelectItem value="leads">Leads</SelectItem>
                        <SelectItem value="all">Both</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Product Type</Label>
                    <Input value={form.audience.product_type} onChange={(e) => updateAudience({ product_type: e.target.value })} placeholder="e.g. Term" data-testid="audience-product-input" />
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Lead Status</Label>
                    <Input value={form.audience.lead_status} onChange={(e) => updateAudience({ lead_status: e.target.value })} placeholder="e.g. Interested" data-testid="audience-status-input" />
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Location</Label>
                    <Input value={form.audience.location} onChange={(e) => updateAudience({ location: e.target.value })} placeholder="e.g. Mumbai" data-testid="audience-location-input" />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Button type="button" size="sm" variant="outline" onClick={previewAudience} data-testid="audience-preview-button">
                    Preview audience size
                  </Button>
                  {audiencePreview && (
                    <p className="text-xs text-slate-700">
                      <strong>{audiencePreview.total}</strong> recipients ({audiencePreview.by_type.clients} clients, {audiencePreview.by_type.leads} leads)
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2"><Label>Schedule (optional)</Label>
                <Input type="datetime-local" value={form.scheduled_for} onChange={(e) => setForm({ ...form, scheduled_for: e.target.value })} data-testid="campaign-schedule-input" />
                <p className="text-[11px] text-slate-500">Leave blank to send immediately when you click "Send Now".</p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button variant="outline" onClick={() => saveDraft(false)} disabled={saving} data-testid="campaign-save-button">
                  {saving ? 'Saving…' : 'Save Draft'}
                </Button>
                <Button onClick={() => saveDraft(true)} disabled={saving} className="bg-blue-800 hover:bg-blue-900" data-testid="campaign-send-now-button">
                  <Send className="h-4 w-4 mr-1.5" /> {saving ? 'Sending…' : 'Send Now'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <div className="flex items-center">
            <Megaphone className="h-5 w-5 text-blue-800 mr-2" />
            <CardTitle style={{ fontFamily: 'Manrope' }}>All Campaigns ({campaigns.length})</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {campaigns.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No campaigns yet. Click "New Campaign" to get started.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Channel</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map(c => (
                  <TableRow key={c.campaign_id} data-testid={`campaign-row-${c.campaign_id}`}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell><Badge variant="outline">{c.channel}</Badge></TableCell>
                    <TableCell><Badge className={badgeForStatus(c.status)}>{c.status}</Badge></TableCell>
                    <TableCell>
                      {c.result ? (
                        <span className="text-xs text-slate-700">
                          <span className="text-green-700 font-semibold">{c.result.sent}</span> sent ·{' '}
                          <span className="text-red-700">{c.result.failed}</span> failed ·{' '}
                          <span className="text-slate-500">{c.result.skipped}</span> skipped
                        </span>
                      ) : <span className="text-xs text-slate-400">—</span>}
                    </TableCell>
                    <TableCell>{c.created_at ? formatDate(c.created_at) : '-'}</TableCell>
                    <TableCell className="text-right">
                      {c.status !== 'sent' && (
                        <Button variant="ghost" size="sm" onClick={() => sendExisting(c.campaign_id)} disabled={sendingId === c.campaign_id} data-testid={`campaign-send-${c.campaign_id}`}>
                          <Send className="h-3.5 w-3.5 mr-1" /> {sendingId === c.campaign_id ? 'Sending…' : 'Send'}
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => openDetail(c.campaign_id)} data-testid={`campaign-view-${c.campaign_id}`} title="View campaign details">
                        <Eye className="h-4 w-4 text-blue-700" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => del(c.campaign_id)} data-testid={`campaign-delete-${c.campaign_id}`} title="Delete campaign">
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="p-3 rounded-lg border border-slate-200 bg-slate-50 flex items-start gap-2">
        <TrendingUp className="h-4 w-4 text-blue-700 mt-0.5" />
        <p className="text-xs text-slate-600">
          Tip: when adding new leads, set the <strong>Campaign Source</strong> field on the lead to track which campaign brought them in.
        </p>
      </div>

      {/* Starter Template Picker */}
      <Dialog open={templatePickerOpen} onOpenChange={setTemplatePickerOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="starter-template-picker">
          <DialogHeader>
            <DialogTitle>Starter Templates</DialogTitle>
            <DialogDescription>Pick a ready-made message. You can still edit it before sending.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {starterTemplates.map((tmpl) => (
              <button
                key={tmpl.id}
                type="button"
                onClick={() => applyStarterTemplate(tmpl)}
                className="text-left p-3 rounded-lg border border-slate-200 hover:border-purple-400 hover:shadow-sm bg-white transition-all"
                data-testid={`starter-template-${tmpl.id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-semibold text-slate-900">{tmpl.name}</p>
                  <Badge variant="outline" className="text-[10px]">{tmpl.category}</Badge>
                </div>
                <div className="flex items-center gap-1 mb-1.5">
                  <Badge className="bg-slate-100 text-slate-700 text-[10px]">{tmpl.channel}</Badge>
                </div>
                <p className="text-[11px] text-slate-600 line-clamp-3 whitespace-pre-line">{tmpl.body}</p>
              </button>
            ))}
          </div>
          {starterTemplates.length === 0 && (
            <p className="text-center text-slate-500 py-8">No templates available.</p>
          )}
        </DialogContent>
      </Dialog>
      {/* Campaign detail modal (view-only) */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg" data-testid="campaign-detail-modal">
          <DialogHeader>
            <DialogTitle>{detailCampaign?.name || 'Campaign detail'}</DialogTitle>
            <DialogDescription>Delivery details of this campaign — read-only.</DialogDescription>
          </DialogHeader>
          {detailCampaign && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-[11px] uppercase text-slate-500">Channel</p><p className="font-medium">{detailCampaign.channel}</p></div>
                <div><p className="text-[11px] uppercase text-slate-500">Status</p><Badge className={badgeForStatus(detailCampaign.status)}>{detailCampaign.status}</Badge></div>
                <div><p className="text-[11px] uppercase text-slate-500">Created</p><p className="font-medium">{detailCampaign.created_at ? new Date(detailCampaign.created_at).toLocaleString('en-IN') : '-'}</p></div>
                <div><p className="text-[11px] uppercase text-slate-500">Sent at</p><p className="font-medium">{detailCampaign.sent_at ? new Date(detailCampaign.sent_at).toLocaleString('en-IN') : '—'}</p></div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-md bg-emerald-50 border border-emerald-100 p-2 text-center"><p className="text-[10px] uppercase text-slate-500">Sent</p><p className="text-lg font-bold text-emerald-700">{detailCampaign.sent_count ?? detailCampaign.sent ?? 0}</p></div>
                <div className="rounded-md bg-red-50 border border-red-100 p-2 text-center"><p className="text-[10px] uppercase text-slate-500">Failed</p><p className="text-lg font-bold text-red-700">{detailCampaign.failed_count ?? detailCampaign.failed ?? 0}</p></div>
                <div className="rounded-md bg-amber-50 border border-amber-100 p-2 text-center"><p className="text-[10px] uppercase text-slate-500">Skipped</p><p className="text-lg font-bold text-amber-700">{detailCampaign.skipped_count ?? detailCampaign.skipped ?? 0}</p></div>
              </div>
              <div>
                <p className="text-[11px] uppercase text-slate-500 mb-1">Message body</p>
                <div className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs whitespace-pre-line max-h-40 overflow-y-auto">{detailCampaign.body || detailCampaign.template_body || '—'}</div>
              </div>
              <div>
                <p className="text-[11px] uppercase text-slate-500 mb-1">Audience filter</p>
                <pre className="rounded-md border border-slate-200 bg-slate-50 p-2 text-[11px] overflow-x-auto max-h-32">{JSON.stringify(detailCampaign.audience || {}, null, 2)}</pre>
              </div>
              {Array.isArray(detailCampaign.recipients) && detailCampaign.recipients.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase text-slate-500 mb-1">Recipients ({detailCampaign.recipients.length})</p>
                  <div className="rounded-md border border-slate-200 max-h-40 overflow-y-auto divide-y">
                    {detailCampaign.recipients.slice(0, 50).map((r, i) => (
                      <div key={i} className="flex items-center justify-between px-2 py-1.5 text-xs">
                        <span className="truncate">{r.name || r.to || r.phone || r.email || '—'}</span>
                        <Badge className={r.status === 'sent' ? 'bg-emerald-100 text-emerald-800' : r.status === 'failed' ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-700'}>{r.status || '—'}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Campaigns;
